
import React, { createContext, useContext, useState, useEffect } from 'react';

// Define user roles
export type UserRole = 'viewer' | 'editor' | 'admin';

// Define user interface
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

// Mock users for demonstration
const MOCK_USERS = [
  { id: '1', email: 'admin', name: 'Admin User', role: 'admin' as UserRole, password: 'admin123' },
  { id: '2', email: 'editor', name: 'Editor User', role: 'editor' as UserRole, password: 'editor123' },
  { id: '3', email: 'viewer', name: 'Viewer User', role: 'viewer' as UserRole, password: 'viewer123' }
];

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (requiredRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('toro_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
  }, []);

  // Simple login function for demo purposes
  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate API call
    const foundUser = MOCK_USERS.find(
      u => u.email === email && u.password === password
    );

    if (foundUser) {
      // Create a sanitized user without password
      const { password, ...userWithoutPassword } = foundUser;
      setUser(userWithoutPassword);
      setIsAuthenticated(true);
      localStorage.setItem('toro_user', JSON.stringify(userWithoutPassword));
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('toro_user');
  };

  // Check if user has required permission based on role hierarchy
  const hasPermission = (requiredRole: UserRole): boolean => {
    if (!user) return false;

    switch (requiredRole) {
      case 'viewer':
        return ['viewer', 'editor', 'admin'].includes(user.role);
      case 'editor':
        return ['editor', 'admin'].includes(user.role);
      case 'admin':
        return user.role === 'admin';
      default:
        return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
